// /hosts — paginated host list. Admin-only on the backend; non-admin users
// will see a 403 here.
//
// Pagination is dead-simple: page number + Prev/Next. We don't ship a per-row
// detail view in v0 — just hostname/fqdn/created/token count.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api, type HostListResponse } from "../lib/api";

const PAGE_SIZE = 50;

export function HostsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<HostListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .hosts(page, PAGE_SIZE)
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setError("Admin role required to view hosts");
        } else {
          setError(err instanceof Error ? err.message : "Failed to load hosts");
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, navigate]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="page">
      <nav className="nav">
        <Link to="/">Home</Link>
        <Link to="/hosts">Hosts</Link>
      </nav>
      <div className="card">
        <div className="toolbar">
          <h1 style={{ margin: 0, fontSize: 20 }}>Hosts</h1>
          {data && (
            <span className="muted">
              {data.total} host{data.total === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {loading && <p className="muted">Loading…</p>}
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}

        {data && !loading && !error && (
          <>
            <table data-testid="hosts-table">
              <thead>
                <tr>
                  <th>FQDN</th>
                  <th>Hostname</th>
                  <th>Created</th>
                  <th style={{ textAlign: "right" }}>Active tokens</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted">
                      No hosts.
                    </td>
                  </tr>
                )}
                {data.items.map((h) => (
                  <tr key={h.id}>
                    <td>{h.fqdn ?? "—"}</td>
                    <td>{h.hostname}</td>
                    <td>{new Date(h.created_at).toLocaleString()}</td>
                    <td style={{ textAlign: "right" }}>{h.token_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="toolbar" style={{ marginTop: 16 }}>
              <span className="muted">
                Page {data.page} of {totalPages}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  className="secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
